import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { useFormContext } from 'react-hook-form'
import { Box, Table, TableBody } from '@mui/material'
import SmallGuide from 'src/components/molecules/SmallGuide'
import { AppTableHead, Column } from 'src/components/tables'

import { FormNameEnum, RowWithSum } from '../../type'
import TenantSalesReportTableRow from '../TenantSalesReportTableRow'
import styles from './styles.module.scss'

export type TenantSalesReportTableProps = {
  orgCode: string
  columns: Column[]
  registerIndex: number
  rows: RowWithSum[]
  onClickValue: (value: string, index: number, childIndex?: number) => void
  showHighlights?: boolean
  expandedRows?: Set<number>
  onToggleRow?: (rowIndex: number) => void
} & React.StyledProps

const TenantSalesReportTable: React.StyledFC<TenantSalesReportTableProps> = ({
  className,
  orgCode,
  columns,
  registerIndex,
  rows,
  onClickValue,
  showHighlights,
  expandedRows,
  onToggleRow,
}: TenantSalesReportTableProps) => {
  const { watch } = useFormContext()
  const [allFiltered, setAllFiltered] = useState(false)
  const filterForMismatch = watch(FormNameEnum.filterForMismatch)

  useEffect(() => {
    let allRowsFiltered = false
    if (filterForMismatch) {
      const filteredRows = rows.filter((row) => Boolean(row.error))
      if (filteredRows.length === 0 && rows.length > 0) {
        allRowsFiltered = true
      }
    }
    setAllFiltered(allRowsFiltered)
  }, [filterForMismatch, rows])

  return (
    <Box className={clsx(className, styles.root)}>
      {allFiltered ? (
        <Box className={styles.allFiltered}>
          <SmallGuide
            icon="kirakira"
            className={styles.guide}
            message="不一致の項目はありません"
            description="報告結果を確認する場合は、フィルタを外して下さい"
          />
        </Box>
      ) : (
        <Table className={styles.table} stickyHeader>
          <AppTableHead columns={columns} />
          <TableBody>
            {rows.map((row, index) => (
              <TenantSalesReportTableRow
                key={row.key}
                className={
                  !Boolean(row.error) && filterForMismatch
                    ? styles.collapseRow
                    : ''
                }
                orgCode={orgCode}
                cells={row.cells}
                inputCell={row.inputCell}
                sumCell={row.sumCell}
                tooltipText={row.tooltipText}
                rowIndex={index}
                registerIndex={registerIndex}
                onClickValue={(value, childIndex) =>
                  onClickValue(value, index, childIndex)
                }
                childRows={row.childRows}
                error={row.error}
                hasDiff={row.hasDiff}
                showHighlights={showHighlights}
                highlightColorIndex={row.highlightColorIndex}
                expanded={expandedRows?.has(index) ?? Boolean(row.error)}
                onToggleExpand={() => onToggleRow?.(index)}
              />
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}

export default TenantSalesReportTable
